import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useRange } from '@/lib/useRange';
import { RANGE_LABELS, type RangeKey } from '@/lib/range';
import { Card, CardLabel } from '@/components/ui/card';
import { StatCard } from '@/components/StatCard';
import { DataTable, type Column } from '@/components/DataTable';
import { DonutChart } from '@/components/charts/DonutChart';
import { StackedBarChart } from '@/components/charts/StackedBarChart';
import { StatusFunnel, type StatusFunnelItem } from '@/components/StatusFunnel';

function api<T>(path: string): () => Promise<T> {
  return () => apiFetch<T>(path);
}

type PostType = 'photo' | 'clip' | 'bubble' | 'story_pic' | 'story_vid' | 'repost';

interface PostsResponse {
  range: RangeKey;
  types: PostType[];
  series: Array<{ date: string } & Record<PostType, number>>;
}

interface StatusFunnelResponse {
  stuckThresholdHours: number;
  totalStuck: number;
  items: StatusFunnelItem[];
}

interface MixResponse {
  items: { name: string; value: number }[];
}

interface AveragesResponse {
  window: RangeKey;
  totalLivePosts: number;
  avgLikes: number;
  avgComments: number;
}

interface TopPosterRow {
  ownerId: string;
  username: string | null;
  name: string | null;
  profilePhoto: string | null;
  posts: number;
  livePosts: number;
}
interface TopPostersResponse {
  range: RangeKey;
  limit: number;
  items: TopPosterRow[];
}

const POST_TYPE_LABELS: Record<PostType, string> = {
  photo: 'Photo',
  clip: 'Clip',
  bubble: 'Bubble',
  story_pic: 'Story (pic)',
  story_vid: 'Story (vid)',
  repost: 'Repost',
};

type TopPosterRowWithRank = TopPosterRow & { rank: number };

const topPosterColumns: Column<TopPosterRowWithRank>[] = [
  {
    key: 'rank',
    header: '#',
    align: 'right',
    width: '50px',
    cell: (r) => (
      <span className="text-fg-subtle font-bold tabular-nums">{r.rank}</span>
    ),
  },
  {
    key: 'owner',
    header: 'Poster',
    cell: (r) => (
      <div className="flex items-center gap-2 min-w-0">
        {r.profilePhoto ? (
          <img
            src={r.profilePhoto}
            alt=""
            className="h-7 w-7 rounded-full object-cover border border-surface-divider"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="h-7 w-7 rounded-full bg-surface-raised border border-surface-divider" />
        )}
        <div className="min-w-0">
          <div className="font-medium truncate">
            {r.username ?? r.name ?? '(unknown)'}
          </div>
          {r.username && r.name && (
            <div className="text-xs text-fg-subtle truncate">{r.name}</div>
          )}
        </div>
      </div>
    ),
  },
  {
    key: 'posts',
    header: 'Posts',
    align: 'right',
    width: '90px',
    cell: (r) => (
      <span className="text-brand-red font-bold tabular-nums">
        {r.posts.toLocaleString()}
      </span>
    ),
  },
  {
    key: 'livePosts',
    header: 'Live',
    align: 'right',
    width: '70px',
    cell: (r) => <span className="tabular-nums">{r.livePosts.toLocaleString()}</span>,
  },
  {
    key: 'liveRate',
    header: 'Live %',
    align: 'right',
    width: '80px',
    cell: (r) => (
      <span className="text-fg-muted tabular-nums">
        {r.posts === 0 ? '—' : `${((r.livePosts / r.posts) * 100).toFixed(0)}%`}
      </span>
    ),
  },
];

export function Content(): JSX.Element {
  const [range] = useRange();
  const errMsg = (q: { error: unknown }): string | null =>
    q.error ? (q.error as Error).message : null;

  const posts = useQuery({
    queryKey: ['metrics', 'content', 'posts', range],
    queryFn: api<PostsResponse>(`/api/metrics/content/posts?range=${range}`),
  });
  const funnel = useQuery({
    queryKey: ['metrics', 'content', 'status-funnel'],
    queryFn: api<StatusFunnelResponse>('/api/metrics/content/status-funnel'),
  });
  const privacy = useQuery({
    queryKey: ['metrics', 'content', 'privacy'],
    queryFn: api<MixResponse>('/api/metrics/content/privacy-distribution'),
  });
  const averages = useQuery({
    queryKey: ['metrics', 'content', 'averages', range],
    queryFn: api<AveragesResponse>(
      `/api/metrics/content/averages?window=${range}`,
    ),
  });
  const topPosters = useQuery({
    queryKey: ['metrics', 'content', 'top-posters', range, 10],
    queryFn: api<TopPostersResponse>(
      `/api/metrics/content/top-posters?range=${range}&limit=10`,
    ),
  });

  const live = funnel.data?.items.find((i) => i.status === 'LIVE')?.count;
  const totalPosts = funnel.data?.items.reduce((s, i) => s + i.count, 0);
  const rankedPosters: TopPosterRowWithRank[] = (topPosters.data?.items ?? []).map(
    (r, i) => ({ ...r, rank: i + 1 }),
  );
  const stuckTotal = funnel.data?.totalStuck ?? 0;
  const stuckThreshold = funnel.data?.stuckThresholdHours ?? 24;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="mb-1 text-2xl font-bold">Content</h1>
        <p className="text-sm text-fg-muted">
          What's being posted on Malsi. KPIs and chart use the range
          ({RANGE_LABELS[range]}); the status funnel reflects current state.
        </p>
      </div>

      {/* Stuck callout */}
      {!funnel.isLoading && stuckTotal > 0 && (
        <div className="flex items-center gap-3 rounded-md border border-state-warning/40 bg-state-warning/10 px-4 py-3">
          <AlertTriangle size={18} className="text-state-warning shrink-0" />
          <div className="text-sm">
            <span className="font-bold text-state-warning">
              {stuckTotal} {stuckTotal === 1 ? 'post is' : 'posts are'} stuck
            </span>{' '}
            <span className="text-fg-muted">
              in a non-LIVE state for more than {stuckThreshold}h. Likely upload
              failures or aborted edits — investigate via the status funnel
              below.
            </span>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total posts"
          value={totalPosts}
          loading={funnel.isLoading}
          error={errMsg(funnel)}
          hint="All statuses, all-time"
        />
        <StatCard
          label="Live posts"
          value={live}
          loading={funnel.isLoading}
          error={errMsg(funnel)}
          hint="status=LIVE"
        />
        <StatCard
          label={`Avg likes / post · ${RANGE_LABELS[range]}`}
          value={
            averages.data ? Number(averages.data.avgLikes.toFixed(2)) : undefined
          }
          loading={averages.isLoading}
          error={errMsg(averages)}
          hint={
            averages.data
              ? `Across ${averages.data.totalLivePosts.toLocaleString()} live posts`
              : undefined
          }
        />
        <StatCard
          label={`Avg comments / post · ${RANGE_LABELS[range]}`}
          value={
            averages.data
              ? Number(averages.data.avgComments.toFixed(2))
              : undefined
          }
          loading={averages.isLoading}
          error={errMsg(averages)}
          hint={
            averages.data
              ? `Across ${averages.data.totalLivePosts.toLocaleString()} live posts`
              : undefined
          }
        />
      </div>

      {/* Posts over time */}
      <Card className="flex flex-col gap-4">
        <div>
          <CardLabel>Posts created</CardLabel>
          <div className="text-lg font-bold mt-1">
            Daily, stacked by post type
          </div>
        </div>
        {posts.isLoading ? (
          <div className="h-[280px] animate-pulse rounded-md bg-surface-divider/30" />
        ) : posts.error ? (
          <div className="text-sm text-state-danger">{errMsg(posts)}</div>
        ) : (
          <StackedBarChart
            data={posts.data?.series ?? []}
            series={(posts.data?.types ?? []).map((t) => ({
              key: t,
              label: POST_TYPE_LABELS[t],
            }))}
          />
        )}
      </Card>

      {/* Status funnel + privacy */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.4fr_1fr]">
        <Card className="flex flex-col gap-4">
          <div>
            <CardLabel>Status funnel</CardLabel>
            <div className="text-lg font-bold mt-1">
              Lifecycle distribution · current state
            </div>
          </div>
          {funnel.isLoading ? (
            <div className="h-[200px] animate-pulse rounded-md bg-surface-divider/30" />
          ) : funnel.error ? (
            <div className="text-sm text-state-danger">{errMsg(funnel)}</div>
          ) : (
            <StatusFunnel
              items={funnel.data?.items ?? []}
              stuckThresholdHours={
                funnel.data?.stuckThresholdHours ?? stuckThreshold
              }
            />
          )}
        </Card>

        <Card className="flex flex-col gap-4">
          <div>
            <CardLabel>Privacy</CardLabel>
            <div className="text-lg font-bold mt-1">
              Distribution across LIVE posts
            </div>
          </div>
          {privacy.isLoading ? (
            <div className="h-[240px] animate-pulse rounded-md bg-surface-divider/30" />
          ) : privacy.error ? (
            <div className="text-sm text-state-danger">{errMsg(privacy)}</div>
          ) : (privacy.data?.items.length ?? 0) === 0 ? (
            <div className="text-sm text-fg-subtle">No live posts yet.</div>
          ) : (
            <DonutChart
              data={privacy.data?.items ?? []}
              centerLabel="Live posts"
            />
          )}
        </Card>
      </div>

      {/* Top posters */}
      <Card className="flex flex-col gap-4">
        <div>
          <CardLabel>Top posters</CardLabel>
          <div className="text-lg font-bold mt-1">
            Most prolific creators · {RANGE_LABELS[range]}
          </div>
        </div>
        <DataTable
          columns={topPosterColumns}
          rows={rankedPosters}
          loading={topPosters.isLoading}
          error={errMsg(topPosters)}
          rowKey={(r) => r.ownerId}
        />
      </Card>
    </div>
  );
}
